import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tab,
  Tabs,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  useTheme,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  UploadFile as UploadIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Phone as PhoneIcon,
  FavoriteBorder as HeartbeatIcon,
  PlayArrow as PlayIcon,
} from "@mui/icons-material";
import axios from "axios";
import { Bot } from "lucide-react";
import {
  PersonalAssistantPreferences,
  PA_DEFAULT_USER_MD,
  PA_DEFAULT_SOUL_MD,
  PA_DEFAULT_MEMORY_MD,
  PA_DEFAULT_IDENTITY_MD,
  PA_DEFAULT_HEARTBEAT_MD,
  PA_DEFAULT_AGENTS_MD,
  PA_DEFAULT_TOOLS_MD,
  DEFAULT_MODEL,
  PERSONAL_ASSISTANT_NAME,
  PERSONAL_ASSISTANT_PERSONA_PRESETS,
  getPersonaAvatarUrl,
  getImageUrlWithSize,
} from "../../constants";
import { hostname } from "../../utils";
import ModelSelector from "../ModelSelector";
import VoiceSelector from "../VoiceSelector";
import RemoteAgentsTab from "./RemoteAgentsTab";
import DailyMemoriesTab from "./DailyMemoriesTab";
import IntegrationsTab from "./IntegrationsTab";
import SkillsTab from "./SkillsTab";

// Generate time options in 15-minute intervals (same as sequence schedule)
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const timeString = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
      const displayTime = new Date(
        `2000-01-01T${timeString}`
      ).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      options.push({ value: timeString, label: displayTime });
    }
  }
  return options;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index} style={{ height: "100%" }}>
    {value === index && <Box sx={{ height: "100%", pt: 2 }}>{children}</Box>}
  </div>
);

interface PersonalAssistantSettingsProps {
  open: boolean;
  onClose: () => void;
  preferences: PersonalAssistantPreferences | null;
  currentPlan: string;
  onSave: (preferences: Partial<PersonalAssistantPreferences>) => Promise<void>;
  loading?: boolean;
  getToken: () => Promise<string>;
  companyId?: string;
}

const PersonalAssistantSettings: React.FC<PersonalAssistantSettingsProps> = ({
  open,
  onClose,
  preferences,
  currentPlan,
  onSave,
  loading = false,
  getToken,
  companyId,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [tabValue, setTabValue] = useState(0);
  const [selectedFile, setSelectedFile] = useState<string>("userMd");
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [userMd, setUserMd] = useState<string>("");
  const [soulMd, setSoulMd] = useState<string>("");
  const [memoryMd, setMemoryMd] = useState<string>("");
  const [identityMd, setIdentityMd] = useState<string>("");
  const [heartbeatMd, setHeartbeatMd] = useState<string>("");
  const [agentsMd, setAgentsMd] = useState<string>("");
  const [toolsMd, setToolsMd] = useState<string>("");
  const [personaName, setPersonaName] = useState<string>("");
  const [personaImageUrl, setPersonaImageUrl] = useState<string>("");
  const [voiceId, setVoiceId] = useState<string | null>(null);
  const [timezone, setTimezone] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [phoneInput, setPhoneInput] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Workspace path state
  const [workspacePath, setWorkspacePath] = useState<string>("");

  // Platform access state
  const [platformAccess, setPlatformAccess] = useState(false);

  // Composio tools state
  const [composioToolsEnabled, setComposioToolsEnabled] = useState(true);

  // Heartbeat state
  const [heartbeatEnabled, setHeartbeatEnabled] = useState(false);
  const [heartbeatInterval, setHeartbeatInterval] = useState("0");
  const [heartbeatSmsEnabled, setHeartbeatSmsEnabled] = useState(false);
  const [heartbeatLastRun, setHeartbeatLastRun] = useState<string | null>(null);
  const [triggeringHeartbeat, setTriggeringHeartbeat] = useState(false);
  // Advanced heartbeat settings
  const [heartbeatTarget, setHeartbeatTarget] = useState("none");
  const [heartbeatLightContext, setHeartbeatLightContext] = useState(false);
  const [heartbeatIsolatedSession, setHeartbeatIsolatedSession] = useState(false);
  const [heartbeatActiveHoursStart, setHeartbeatActiveHoursStart] = useState<string | null>(null);
  const [heartbeatActiveHoursEnd, setHeartbeatActiveHoursEnd] = useState<string | null>(null);

  const HEARTBEAT_INTERVAL_OPTIONS: Record<string, string> = {
    "0": "Disabled",
    "5m": "Every 5 minutes",
    "15m": "Every 15 minutes",
    "30m": "Every 30 minutes",
    "1h": "Every hour",
    "2h": "Every 2 hours",
    "4h": "Every 4 hours",
    "8h": "Every 8 hours",
    "12h": "Every 12 hours",
    "24h": "Every 24 hours",
  };

  const HEARTBEAT_TARGET_OPTIONS: Record<string, string> = {
    "none": "Silent (no notifications)",
    "email": "Email (free)",
    "sms": "SMS (1 credit/message)",
    "web": "Web chat only",
  };

  // Initialize form from preferences - use defaults if fields are empty
  useEffect(() => {
    if (preferences) {
      setSelectedModel(preferences.defaultModel || DEFAULT_MODEL);
      setUserMd(preferences.userMd || preferences.defaults?.userMd || PA_DEFAULT_USER_MD);
      setSoulMd(preferences.soulMd || preferences.defaults?.soulMd || PA_DEFAULT_SOUL_MD);
      setMemoryMd(preferences.memoryMd || preferences.defaults?.memoryMd || PA_DEFAULT_MEMORY_MD);
      setIdentityMd(preferences.identityMd || preferences.defaults?.identityMd || PA_DEFAULT_IDENTITY_MD);
      setHeartbeatMd(preferences.heartbeatMd || preferences.defaults?.heartbeatMd || PA_DEFAULT_HEARTBEAT_MD);
      setAgentsMd(preferences.agentsMd || preferences.defaults?.agentsMd || PA_DEFAULT_AGENTS_MD);
      setToolsMd(preferences.toolsMd || preferences.defaults?.toolsMd || PA_DEFAULT_TOOLS_MD);
      setPersonaName(preferences.personaName || "");
      setPersonaImageUrl(preferences.personaImageUrl || "");
      setVoiceId(preferences.voiceId || null);
      setTimezone(preferences.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "");
      setPhoneNumber(preferences.phoneNumber || "");
      setPhoneInput(preferences.phoneNumber || "");
      setIsPhoneVerified(!!preferences.phoneNumber);
      setVerificationSent(false);
      setVerificationCode("");
      setPhoneError(null);
      setHasChanges(false);

      // Load workspace settings
      setWorkspacePath(preferences.workspacePath || "");

      // Load platform access settings
      setPlatformAccess(preferences.platformAccess || false);

      // Load Composio tools settings
      setComposioToolsEnabled(preferences.composioToolsEnabled !== false); // Default to true

      // Load heartbeat settings
      setHeartbeatEnabled(preferences.heartbeatEnabled || false);
      setHeartbeatInterval(preferences.heartbeatInterval || "0");
      setHeartbeatSmsEnabled(preferences.heartbeatSmsEnabled || false);
      setHeartbeatLastRun(preferences.heartbeatLastRun || null);
      // Advanced heartbeat settings
      setHeartbeatTarget(preferences.heartbeatTarget || "none");
      setHeartbeatLightContext(preferences.heartbeatLightContext || false);
      setHeartbeatIsolatedSession(preferences.heartbeatIsolatedSession || false);
      setHeartbeatActiveHoursStart(preferences.heartbeatActiveHoursStart || null);
      setHeartbeatActiveHoursEnd(preferences.heartbeatActiveHoursEnd || null);
    }
  }, [preferences]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Note: phoneNumber is saved during verification, not here
      await onSave({
        defaultModel: selectedModel || null,
        userMd: userMd || null,
        soulMd: soulMd || null,
        memoryMd: memoryMd || null,
        identityMd: identityMd || null,
        heartbeatMd: heartbeatMd || null,
        agentsMd: agentsMd || null,
        toolsMd: toolsMd || null,
        personaName: personaName || null,
        personaImageUrl: personaImageUrl || null,
        voiceId: voiceId || null,
        timezone: timezone || null,
        heartbeatEnabled: heartbeatEnabled,
        heartbeatInterval: heartbeatInterval,
        heartbeatSmsEnabled: heartbeatSmsEnabled,
        heartbeatTarget: heartbeatTarget,
        heartbeatLightContext: heartbeatLightContext,
        heartbeatIsolatedSession: heartbeatIsolatedSession,
        heartbeatActiveHoursStart: heartbeatActiveHoursStart,
        heartbeatActiveHoursEnd: heartbeatActiveHoursEnd,
        workspacePath: workspacePath || null,
        platformAccess: platformAccess,
        composioToolsEnabled: composioToolsEnabled,
      });
      setHasChanges(false);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file (PNG, JPG, etc.)");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError("Image size should be less than 2MB");
      return;
    }

    setUploadingImage(true);
    setError(null);

    try {
      const token = await getToken();

      const formData = new FormData();
      formData.append("image", file);
      formData.append("name", "Executive Assistant Avatar");
      formData.append("category", "personal-assistant");
      formData.append("public", "true");

      const response = await axios.post(`${hostname}/platform-images`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 201) {
        setPersonaImageUrl(response.data.url);
        setHasChanges(true);
      }
    } catch (err) {
      console.error("Error uploading image:", err);
      setError("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveImage = () => {
    setPersonaImageUrl("");
    setHasChanges(true);
  };

  const handleModelChange = (value: string) => {
    setSelectedModel(value);
    setHasChanges(true);
  };

  const handleTextChange = (
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setter(e.target.value);
    setHasChanges(true);
  };

  const handleResetToDefault = (field: string) => {
    switch (field) {
      case "userMd":
        setUserMd(preferences?.defaults?.userMd || PA_DEFAULT_USER_MD);
        break;
      case "soulMd":
        setSoulMd(preferences?.defaults?.soulMd || PA_DEFAULT_SOUL_MD);
        break;
      case "memoryMd":
        setMemoryMd(preferences?.defaults?.memoryMd || PA_DEFAULT_MEMORY_MD);
        break;
      case "identityMd":
        setIdentityMd(preferences?.defaults?.identityMd || PA_DEFAULT_IDENTITY_MD);
        break;
      case "heartbeatMd":
        setHeartbeatMd(preferences?.defaults?.heartbeatMd || PA_DEFAULT_HEARTBEAT_MD);
        break;
      case "agentsMd":
        setAgentsMd(preferences?.defaults?.agentsMd || PA_DEFAULT_AGENTS_MD);
        break;
      case "toolsMd":
        setToolsMd(preferences?.defaults?.toolsMd || PA_DEFAULT_TOOLS_MD);
        break;
    }
    setHasChanges(true);
  };

  const handleSendVerificationCode = async () => {
    if (!phoneInput.trim()) {
      setPhoneError("Please enter a phone number");
      return;
    }

    setSendingCode(true);
    setPhoneError(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/personal-assistant/phone/send-code`,
        { phoneNumber: phoneInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setVerificationSent(true);
        setVerificationCode("");
      }
    } catch (err: any) {
      setPhoneError(err.response?.data?.error || "Failed to send verification code");
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setPhoneError("Please enter the verification code");
      return;
    }

    setVerifyingCode(true);
    setPhoneError(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/personal-assistant/phone/verify`,
        { phoneNumber: phoneInput, code: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setPhoneNumber(response.data.phoneNumber);
        setPhoneInput(response.data.phoneNumber);
        setIsPhoneVerified(true);
        setVerificationSent(false);
        setVerificationCode("");
      }
    } catch (err: any) {
      setPhoneError(err.response?.data?.error || "Failed to verify code");
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleChangePhoneNumber = () => {
    setIsPhoneVerified(false);
    setVerificationSent(false);
    setVerificationCode("");
    setPhoneError(null);
  };

  const handleTriggerHeartbeat = async () => {
    setTriggeringHeartbeat(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/personal-assistant/heartbeat/trigger`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === "completed") {
        setHeartbeatLastRun(new Date().toISOString());
      } else if (response.data.status === "skipped") {
        setError("No heartbeat tasks configured. Add tasks to HEARTBEAT.md first.");
      } else if (response.data.status === "error") {
        setError(response.data.message || "Heartbeat failed");
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to trigger heartbeat");
    } finally {
      setTriggeringHeartbeat(false);
    }
  };

  const formatLastRun = (isoString: string | null) => {
    if (!isoString) return "Never";
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: "90vh", maxHeight: 900 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Assistant Settings
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mx: 3, mt: 2 }}>
                {error}
              </Alert>
            )}

            {/* Model Selector */}
            <Box sx={{ px: 3, pt: 2 }}>
              <ModelSelector
                currentPlan={currentPlan}
                value={selectedModel}
                onChange={(e) => handleModelChange(e.target.value)}
                tools={true}
                sx={{ mt: 0 }}
              />
            </Box>

            {/* Tabs for settings */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, mt: 2 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                <Tab label="General" />
                <Tab label="Voice & Phone" />
                <Tab label="Heartbeat" />
                <Tab label="Memories" />
                <Tab label="Skills" />
                <Tab label="Integrations" />
                <Tab label="Files" />
                <Tab label="Workstation" />
              </Tabs>
            </Box>

            {/* Tab panels */}
            <Box sx={{ flex: 1, overflow: "auto", px: 3, pb: 2 }}>
              {/* General Tab */}
              <TabPanel value={tabValue} index={0}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {/* Persona Section */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Assistant Persona
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
                      Choose a preset or customize your assistant's identity. Use SOUL.md for personality.
                    </Typography>

                    {/* Preset Selector */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                      Choose a Persona
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        justifyContent: "flex-start",
                      }}
                    >
                      {PERSONAL_ASSISTANT_PERSONA_PRESETS.map((preset) => {
                        const isSelected =
                          personaName === preset.name ||
                          (!personaName && preset.name === PERSONAL_ASSISTANT_NAME);
                        // Use 88px thumbnails (2x for retina at 44px display) for faster loading
                        const presetImageUrl = getPersonaAvatarUrl(preset.imageId, hostname, 88);
                        // Store the base URL without size when selecting (for flexibility at display time)
                        const presetImageUrlForStorage = getPersonaAvatarUrl(preset.imageId, hostname);
                        return (
                          <Box
                            key={preset.name}
                            onClick={() => {
                              setPersonaName(preset.name === PERSONAL_ASSISTANT_NAME ? "" : preset.name);
                              // Save base URL without size for flexibility at display time
                              setPersonaImageUrl(presetImageUrlForStorage || "");
                              setHasChanges(true);
                            }}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.5,
                              p: 0.75,
                              borderRadius: 1.5,
                              cursor: "pointer",
                              border: 2,
                              borderColor: isSelected ? "primary.main" : "transparent",
                              backgroundColor: isSelected
                                ? "action.selected"
                                : "action.hover",
                              transition: "all 0.2s",
                              "&:hover": {
                                backgroundColor: "action.selected",
                              },
                              width: { xs: 70, sm: 80 },
                              minWidth: { xs: 70, sm: 80 },
                              maxWidth: { xs: 70, sm: 80 },
                              flexShrink: 0,
                            }}
                          >
                            <Avatar
                              src={presetImageUrl || undefined}
                              sx={{
                                width: 44,
                                height: 44,
                                backgroundColor: isDark ? "#333333" : "#e0e0e0",
                                color: theme.palette.text.primary,
                              }}
                            >
                              {!presetImageUrl && <Bot size={22} />}
                            </Avatar>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: isSelected ? 600 : 400,
                                textAlign: "center",
                                fontSize: "0.7rem",
                                lineHeight: 1.2,
                              }}
                            >
                              {preset.name}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Custom Name & Avatar Section */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                      Or Customize
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
                      <Box sx={{ position: "relative" }}>
                        <Avatar
                          src={getImageUrlWithSize(personaImageUrl, 128) || undefined}
                          sx={{
                            width: 64,
                            height: 64,
                            backgroundColor: isDark ? "#333333" : "#e0e0e0",
                            color: theme.palette.text.primary,
                          }}
                        >
                          {!personaImageUrl && <Bot size={32} />}
                        </Avatar>
                        {uploadingImage && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "rgba(0,0,0,0.5)",
                              borderRadius: "50%",
                            }}
                          >
                            <CircularProgress size={20} sx={{ color: "white" }} />
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
                        <TextField
                          label="Custom Name"
                          size="small"
                          fullWidth
                          value={personaName}
                          onChange={handleTextChange(setPersonaName)}
                          placeholder={PERSONAL_ASSISTANT_NAME}
                          inputProps={{ maxLength: 100 }}
                        />
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<UploadIcon />}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingImage}
                          >
                            Upload Avatar
                          </Button>
                          {personaImageUrl && (
                            <Button
                              variant="text"
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={handleRemoveImage}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          PNG or JPG, max 2MB
                        </Typography>
                      </Box>
                      <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleImageUpload}
                      />
                    </Box>
                  </Box>
                  </Box>

                  {/* Timezone Selection */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                      Your Timezone
                    </Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel>Timezone</InputLabel>
                      <Select
                        value={timezone}
                        label="Timezone"
                        onChange={(e) => {
                          setTimezone(e.target.value);
                          setHasChanges(true);
                        }}
                      >
                        {(Intl as typeof Intl & { supportedValuesOf: (key: string) => string[] }).supportedValuesOf("timeZone").map((tz: string) => (
                          <MenuItem key={tz} value={tz}>
                            {tz.replace(/_/g, " ")}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      Used for timestamps, scheduling, and daily memory logs
                    </Typography>
                  </Box>
                </Box>
              </TabPanel>

              {/* Voice & Phone Tab */}
              <TabPanel value={tabValue} index={1}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Configure voice settings for text-to-speech and phone authentication.
                  </Typography>

                  {/* Voice Selection */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                      Assistant Voice
                    </Typography>
                    <VoiceSelector
                      value={voiceId}
                      onChange={(newVoiceId) => {
                        setVoiceId(newVoiceId);
                        setHasChanges(true);
                      }}
                      getToken={getToken}
                      companyId={companyId}
                      label="Select Voice"
                      helperText="Choose a voice for your assistant's responses in voice mode"
                      showTestButton={true}
                      valueType="voiceId"
                    />
                  </Box>

                  {/* Phone Number Verification */}
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <Typography variant="subtitle2">
                        Phone Number
                      </Typography>
                      <Chip label="1 Credit/Message" size="small" />
                    </Box>

                    {phoneError && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPhoneError(null)}>
                        {phoneError}
                      </Alert>
                    )}

                    {isPhoneVerified ? (
                      // Verified state
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 2,
                            borderRadius: 1,
                            backgroundColor: "action.hover",
                            flex: 1,
                          }}
                        >
                          <CheckIcon color="primary" />
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {phoneNumber}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Verified
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleChangePhoneNumber}
                        >
                          Change
                        </Button>
                      </Box>
                    ) : verificationSent ? (
                      // Verification code entry state
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Alert severity="info">
                          We sent a verification code to {phoneInput}
                        </Alert>
                        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                          <TextField
                            label="Verification Code"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            placeholder="123456"
                            inputProps={{ maxLength: 6 }}
                            sx={{ width: 200 }}
                          />
                          <Button
                            variant="contained"
                            onClick={handleVerifyCode}
                            disabled={verifyingCode || !verificationCode.trim()}
                            sx={{ height: 56 }}
                          >
                            {verifyingCode ? <CircularProgress size={20} /> : "Verify"}
                          </Button>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            onClick={handleSendVerificationCode}
                            disabled={sendingCode}
                          >
                            {sendingCode ? "Sending..." : "Resend Code"}
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              setVerificationSent(false);
                              setVerificationCode("");
                              setPhoneError(null);
                            }}
                          >
                            Change Number
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      // Initial phone entry state
                      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                        <TextField
                          fullWidth
                          label="Your Phone Number"
                          value={phoneInput}
                          onChange={(e) => setPhoneInput(e.target.value)}
                          placeholder="+1 (555) 123-4567"
                          helperText="We'll send a verification code to confirm your number"
                          inputProps={{ maxLength: 50 }}
                          InputProps={{
                            startAdornment: <PhoneIcon sx={{ mr: 1, color: "action.active" }} />,
                          }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleSendVerificationCode}
                          disabled={sendingCode || !phoneInput.trim()}
                          sx={{ height: 56, minWidth: 140 }}
                        >
                          {sendingCode ? <CircularProgress size={20} /> : "Send Code"}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              </TabPanel>

              {/* Heartbeat Tab */}
              <TabPanel value={tabValue} index={2}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Periodically wake your assistant to check HEARTBEAT.md tasks (calendar, emails, reminders).
                  </Typography>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Interval</InputLabel>
                        <Select
                          value={heartbeatInterval}
                          label="Interval"
                          onChange={(e) => {
                            const newInterval = e.target.value;
                            setHeartbeatInterval(newInterval);
                            setHeartbeatEnabled(newInterval !== "0");
                            setHasChanges(true);
                          }}
                        >
                          {Object.entries(HEARTBEAT_INTERVAL_OPTIONS).map(([key, label]) => (
                            <MenuItem key={key} value={key}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={triggeringHeartbeat ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={handleTriggerHeartbeat}
                        disabled={triggeringHeartbeat}
                      >
                        Run Now
                      </Button>

                      {heartbeatLastRun && (
                        <Typography variant="caption" color="text.secondary">
                          Last run: {formatLastRun(heartbeatLastRun)}
                        </Typography>
                      )}
                    </Box>

                    {heartbeatEnabled && (
                      <>
                        {/* Target (notification delivery) */}
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Notification Target
                          </Typography>
                          <FormControl size="small" sx={{ minWidth: 250 }}>
                            <Select
                              value={heartbeatTarget}
                              onChange={(e) => {
                                setHeartbeatTarget(e.target.value);
                                setHasChanges(true);
                              }}
                            >
                              {Object.entries(HEARTBEAT_TARGET_OPTIONS).map(([key, label]) => (
                                <MenuItem
                                  key={key}
                                  value={key}
                                  disabled={key === "sms" && !isPhoneVerified}
                                >
                                  {label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                            Where to deliver notifications when the assistant has something to report
                          </Typography>
                        </Box>

                        {/* Active Hours */}
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Active Hours
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>From</InputLabel>
                              <Select
                                value={heartbeatActiveHoursStart || ""}
                                label="From"
                                onChange={(e) => {
                                  setHeartbeatActiveHoursStart(e.target.value || null);
                                  setHasChanges(true);
                                }}
                              >
                                <MenuItem value="">
                                  <em>Any time</em>
                                </MenuItem>
                                {generateTimeOptions().map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Typography variant="body2" color="text.secondary">to</Typography>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Until</InputLabel>
                              <Select
                                value={heartbeatActiveHoursEnd || ""}
                                label="Until"
                                onChange={(e) => {
                                  setHeartbeatActiveHoursEnd(e.target.value || null);
                                  setHasChanges(true);
                                }}
                              >
                                <MenuItem value="">
                                  <em>Any time</em>
                                </MenuItem>
                                {generateTimeOptions().map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                            Leave blank for 24/7 operation
                          </Typography>
                        </Box>

                        {/* Advanced options */}
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Advanced Options
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={heartbeatLightContext}
                                  onChange={(e) => {
                                    setHeartbeatLightContext(e.target.checked);
                                    setHasChanges(true);
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">Light context</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Only inject HEARTBEAT.md (saves tokens)
                                  </Typography>
                                </Box>
                              }
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={heartbeatIsolatedSession}
                                  onChange={(e) => {
                                    setHeartbeatIsolatedSession(e.target.checked);
                                    setHasChanges(true);
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">Isolated session</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Fresh thread each run (no conversation history)
                                  </Typography>
                                </Box>
                              }
                            />
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              </TabPanel>

              {/* Memories Tab */}
              <TabPanel value={tabValue} index={3}>
                <DailyMemoriesTab getToken={getToken} />
              </TabPanel>

              {/* Skills Tab */}
              <TabPanel value={tabValue} index={4}>
                <SkillsTab getToken={getToken} />
              </TabPanel>

              {/* Integrations Tab */}
              <TabPanel value={tabValue} index={5}>
                <IntegrationsTab
                  getToken={getToken}
                  platformAccess={platformAccess}
                  onPlatformAccessChange={(enabled) => {
                    setPlatformAccess(enabled);
                    setHasChanges(true);
                  }}
                  composioToolsEnabled={composioToolsEnabled}
                  onComposioToolsEnabledChange={(enabled) => {
                    setComposioToolsEnabled(enabled);
                    setHasChanges(true);
                  }}
                />
              </TabPanel>

              {/* Files Tab */}
              <TabPanel value={tabValue} index={6}>
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  {/* File selector chips */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    {[
                      { key: "userMd", label: "USER.md", desc: "About you" },
                      { key: "soulMd", label: "SOUL.md", desc: "Personality" },
                      { key: "memoryMd", label: "MEMORY.md", desc: "Memories" },
                      { key: "identityMd", label: "IDENTITY.md", desc: "Identity" },
                      { key: "heartbeatMd", label: "HEARTBEAT.md", desc: "Tasks" },
                      { key: "agentsMd", label: "AGENTS.md", desc: "Behavior" },
                      { key: "toolsMd", label: "TOOLS.md", desc: "Tools" },
                    ].map((file) => (
                      <Chip
                        key={file.key}
                        label={file.label}
                        onClick={() => setSelectedFile(file.key)}
                        color={selectedFile === file.key ? "primary" : "default"}
                        variant={selectedFile === file.key ? "filled" : "outlined"}
                        sx={{ cursor: "pointer" }}
                      />
                    ))}
                  </Box>

                  {/* File description and reset button */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {selectedFile === "userMd" && "Tell the assistant about yourself - your name, timezone, role, and context."}
                      {selectedFile === "soulMd" && "Customize the assistant's personality, style, and boundaries."}
                      {selectedFile === "memoryMd" && "Important facts, preferences, and context to remember across conversations."}
                      {selectedFile === "identityMd" && "Define your assistant's name, creature type, vibe, and emoji."}
                      {selectedFile === "heartbeatMd" && "Periodic tasks for the assistant to check during heartbeats."}
                      {selectedFile === "agentsMd" && "Workspace behavior guidelines - safety rules and external action policies."}
                      {selectedFile === "toolsMd" && "Tool configurations, connected services, and platform-specific notes."}
                    </Typography>
                    <Button size="small" onClick={() => handleResetToDefault(selectedFile)}>
                      Reset to Template
                    </Button>
                  </Box>

                  {/* File editor - grows to fill available space */}
                  <Box sx={{ flex: 1, minHeight: 300, display: "flex", flexDirection: "column" }}>
                    {selectedFile === "userMd" && (
                      <TextField
                        multiline
                        fullWidth
                        value={userMd}
                        onChange={handleTextChange(setUserMd)}
                        placeholder={PA_DEFAULT_USER_MD}
                        sx={{ flex: 1, "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start" }, "& textarea": { height: "100% !important", overflow: "auto !important" } }}
                        InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.875rem", height: "100%" } }}
                      />
                    )}
                    {selectedFile === "soulMd" && (
                      <TextField
                        multiline
                        fullWidth
                        value={soulMd}
                        onChange={handleTextChange(setSoulMd)}
                        placeholder={PA_DEFAULT_SOUL_MD}
                        sx={{ flex: 1, "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start" }, "& textarea": { height: "100% !important", overflow: "auto !important" } }}
                        InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.875rem", height: "100%" } }}
                      />
                    )}
                    {selectedFile === "memoryMd" && (
                      <TextField
                        multiline
                        fullWidth
                        value={memoryMd}
                        onChange={handleTextChange(setMemoryMd)}
                        placeholder={PA_DEFAULT_MEMORY_MD}
                        sx={{ flex: 1, "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start" }, "& textarea": { height: "100% !important", overflow: "auto !important" } }}
                        InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.875rem", height: "100%" } }}
                      />
                    )}
                    {selectedFile === "identityMd" && (
                      <TextField
                        multiline
                        fullWidth
                        value={identityMd}
                        onChange={handleTextChange(setIdentityMd)}
                        placeholder={PA_DEFAULT_IDENTITY_MD}
                        sx={{ flex: 1, "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start" }, "& textarea": { height: "100% !important", overflow: "auto !important" } }}
                        InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.875rem", height: "100%" } }}
                      />
                    )}
                    {selectedFile === "heartbeatMd" && (
                      <TextField
                        multiline
                        fullWidth
                        value={heartbeatMd}
                        onChange={handleTextChange(setHeartbeatMd)}
                        placeholder={PA_DEFAULT_HEARTBEAT_MD}
                        sx={{ flex: 1, "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start" }, "& textarea": { height: "100% !important", overflow: "auto !important" } }}
                        InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.875rem", height: "100%" } }}
                      />
                    )}
                    {selectedFile === "agentsMd" && (
                      <TextField
                        multiline
                        fullWidth
                        value={agentsMd}
                        onChange={handleTextChange(setAgentsMd)}
                        placeholder={PA_DEFAULT_AGENTS_MD}
                        sx={{ flex: 1, "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start" }, "& textarea": { height: "100% !important", overflow: "auto !important" } }}
                        InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.875rem", height: "100%" } }}
                      />
                    )}
                    {selectedFile === "toolsMd" && (
                      <TextField
                        multiline
                        fullWidth
                        value={toolsMd}
                        onChange={handleTextChange(setToolsMd)}
                        placeholder={PA_DEFAULT_TOOLS_MD}
                        sx={{ flex: 1, "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start" }, "& textarea": { height: "100% !important", overflow: "auto !important" } }}
                        InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.875rem", height: "100%" } }}
                      />
                    )}
                  </Box>
                </Box>
              </TabPanel>

              {/* Workstation Tab */}
              <TabPanel value={tabValue} index={7}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {/* Workspace Path Setting */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Workspace Path
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={workspacePath}
                      onChange={(e) => {
                        setWorkspacePath(e.target.value);
                        setHasChanges(true);
                      }}
                      placeholder="~/workspace"
                      helperText="Directory where skills are synced and commands run. Default: ~/workspace"
                    />
                  </Box>

                  {/* Remote Agents */}
                  <RemoteAgentsTab getToken={getToken} />
                </Box>
              </TabPanel>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || !hasChanges}
        >
          {saving ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PersonalAssistantSettings;
